import Slider, { SliderProps } from '@mui/material/Slider'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useEffect, useMemo, useState } from 'react'
import {
  deriveCanStoreVehicle,
  deriveLocationTypes,
  SPACE_TYPE_MAP
} from 'src/components/SearchBar/SpaceTypeFilters'
import { SpaceType } from 'src/components/SearchBar/types'
import useCommon from 'src/hooks/useCommon'
import ga from 'src/utils/pixels/gtag'
import { DEFAULT_ZOOM } from '../../constants'
import { useFilterProvider } from '../../context/FilterProvider'
import useBreakpoints from '../../hooks/useBreakpoints'
import { SizeFilter, SortBy } from '../../types/graphql'
import { IQueryParams } from '../../types/types'
import {
  convertZoomToRangeString,
  MAX_PRICE,
  MIN_PRICE,
  numberWithCommas,
  valueLabelFormat
} from '../../utils'
import BaseSelect from '../Inputs/BaseSelect'
import ModalDrawer from '../ModalDrawer'
import { StyledDrawer } from './styled'

type IFilters = Pick<
  IQueryParams,
  'canStoreVehicle' | 'sizes' | 'minPrice' | 'maxPrice' | 'zoom' | 'sortBy'
>

// Which container the full filter panel rendered in — required (never
// defaulted inline) so PostHog can answer "do mobile users apply filters
// from the drawer at the same rate desktop users do from the modal".
// See CLAUDE.md "Instrument public-page interactions by default".
export type FiltersSurface = 'mobile_drawer' | 'desktop_modal'

type IFiltersProps = {
  setFiltersOpen: (open: boolean) => void
  surface: FiltersSurface
}

const PanelSlider = (props: SliderProps) => {
  const { className, ...rest } = props
  return (
    <Slider
      className={`text-[#D1E9F4] h-[5px] [&_.MuiSlider-track]:border-none [&_.MuiSlider-track]:text-[#00A4FF] [&_.MuiSlider-rail]:opacity-100 [&_.MuiSlider-thumb]:h-4 [&_.MuiSlider-thumb]:w-4 [&_.MuiSlider-thumb]:bg-[#00A4FF] [&_.MuiSlider-thumb]:border-2 [&_.MuiSlider-thumb]:border-solid [&_.MuiSlider-thumb]:border-[#FFFFFF] [&_.MuiSlider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.2)] [&_.MuiSlider-thumb:before]:hidden [&_.MuiSlider-thumb:hover]:shadow-[0_0_0_6px_rgba(0,164,255,0.125)] [&_.MuiSlider-thumb.Mui-focusVisible]:shadow-[0_0_0_6px_rgba(0,164,255,0.125)] ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

function Filters({ setFiltersOpen, surface }: IFiltersProps) {
  const { t } = useTranslation('common')
  const { filters, updateParams } = useFilterProvider()
  const sortByOptions = t('SORT_BY', { returnObjects: true })

  const [state, setState] = useState<IFilters>({
    canStoreVehicle: filters.canStoreVehicle,
    sizes: filters.sizes,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice ?? MAX_PRICE,
    zoom: filters.zoom ?? DEFAULT_ZOOM,
    sortBy: filters.sortBy ?? SortBy.Recommended
  })

  const onSubmit = () => {
    const { minPrice, maxPrice, zoom, sizes, sortBy } = state

    const applySize = !isEqual(sizes, filters.sizes)
    const applyMinPrice = !isEqual(minPrice, filters.minPrice)
    const applyMaxPrice = !isEqual(maxPrice, filters.maxPrice)
    const applyZoom = !isEqual(zoom, filters.zoom)
    const applySortBy = !isEqual(sortBy, filters.sortBy)

    // When sizes change, derive canStoreVehicle AND locationTypes from the
    // selected sizes. Without locationTypes, the backend's area-based size
    // filter admits listings of the wrong type (e.g. mini-bodegas in
    // PARKING+NAVE searches).
    const sizeParams = applySize
      ? {
          sizes,
          canStoreVehicle: deriveCanStoreVehicle(sizes),
          locationTypes: deriveLocationTypes(sizes)
        }
      : {}

    updateParams({
      ...sizeParams,
      ...(applyMinPrice && { minPrice }),
      ...(applyMaxPrice && { maxPrice }),
      ...(applyZoom && { zoom }),
      ...(applySortBy && { sortBy }),
      page: undefined
    })

    if (applySize) {
      ga.event('select_category', {
        filter: 'size',
        value: sizes?.map((size) => sizeMapValues[size]).join(', '),
        surface
      })
    }

    if (applyMinPrice || applyMaxPrice) {
      ga.event('select_category', {
        filter: 'price',
        value: `${minPrice}-${maxPrice}`,
        surface
      })
    }

    if (applyZoom) {
      ga.event('select_category', {
        filter: 'distance',
        value: convertZoomToRangeString(zoom ?? DEFAULT_ZOOM),
        surface
      })
    }

    if (applySortBy) {
      ga.event('select_category', {
        filter: 'sort',
        value: sortBy,
        surface
      })
    }

    setFiltersOpen(false)
  }

  const onClear = () => {
    setState({
      canStoreVehicle: undefined,
      sizes: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      zoom: undefined,
      sortBy: SortBy.Recommended
    })
    updateParams({
      canStoreVehicle: undefined,
      sizes: undefined,
      locationTypes: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      zoom: undefined,
      sortBy: SortBy.Recommended,
      page: undefined
    })
    setFiltersOpen(false)
  }

  const { sizeMapValues } = useCommon()

  const filteredSizeOptions = useMemo(() => {
    const spaceTypes = filters.spaceTypes
    if (
      !spaceTypes ||
      spaceTypes.length === 0 ||
      spaceTypes.includes(SpaceType.ALL)
    ) {
      return sizeMapValues
    }
    const relevantSizes = new Set<string>()
    spaceTypes.forEach((st) => {
      SPACE_TYPE_MAP[st]?.query?.sizes?.forEach((s) => relevantSizes.add(s))
    })
    if (relevantSizes.size === 0) return sizeMapValues
    return Object.fromEntries(
      Object.entries(sizeMapValues).filter(([key]) => relevantSizes.has(key))
    )
  }, [filters.spaceTypes, sizeMapValues])

  const isSizesFiltered =
    !!filters.spaceTypes &&
    filters.spaceTypes.length > 0 &&
    !filters.spaceTypes.includes(SpaceType.ALL) &&
    Object.keys(filteredSizeOptions).length < Object.keys(sizeMapValues).length

  const handleShowAllSizes = () => {
    updateParams({ spaceTypes: undefined, page: undefined })
  }

  return (
    <div className="flex h-full flex-col bg-white-500">
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {/* Size Filter Section */}
        <section className="mb-6">
          <h3 className="mb-4 text-lg font-semibold text-ink-800">
            {t('FILTER.SIZE')}
          </h3>
          <div className="space-y-1">
            {Object.entries(filteredSizeOptions).map(([key, label]) => {
              const isChecked = state.sizes?.includes(key as SizeFilter)
              const desc = (
                t('SIZE_FILTER_DESC', { returnObjects: true }) as Record<
                  string,
                  string
                >
              )[key]
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    const current = state.sizes || []
                    const next = current.includes(key as SizeFilter)
                      ? current.filter((s) => s !== key)
                      : [...current, key as SizeFilter]
                    setState({ ...state, sizes: next })
                  }}
                  className="flex w-full items-start gap-3 rounded-xl px-2 py-3 text-left transition-colors hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                >
                  <div
                    className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                      isChecked
                        ? 'border-blue-800 bg-blue-800'
                        : 'border-ink-100/40 bg-white-500'
                    }`}
                  >
                    {isChecked && (
                      <svg
                        className="size-3 text-white-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <span className="block text-base font-semibold text-ink-800">
                      {label}
                    </span>
                    {desc && (
                      <span className="mt-0.5 block text-sm text-ink-200">
                        {desc}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {isSizesFiltered && (
              <button
                type="button"
                onClick={handleShowAllSizes}
                className="flex items-center gap-1.5 rounded text-sm font-medium text-ink-200 transition-colors hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
              >
                <i className="si si-sliders text-xs" />
                {t('FILTER.SHOW_ALL_SIZES')}
              </button>
            )}
            <Link
              href="/guia-de-tamanos"
              className="inline-block rounded text-sm font-semibold text-blue-800 underline decoration-blue-800/30 underline-offset-2 hover:decoration-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
            >
              {t('FILTER.SIZE_GUIDE')} - {t('FILTER.SIZE_GUIDE_CTA')}
            </Link>
          </div>
        </section>

        {/* Price Range Section */}
        <section className="mb-6">
          <h3 className="mb-4 text-lg font-semibold text-ink-800">
            {t('FILTER.RENT_BY_MONTH')}
          </h3>
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex-1 rounded-xl border border-ink-100/20 px-3 py-2.5">
              <span className="block text-[10px] font-medium tracking-wider text-ink-200 uppercase">
                {t('FILTER.FROM_PRICE')}
              </span>
              <span className="block text-sm font-semibold text-ink-800">
                {!state.minPrice || state.minPrice <= 0
                  ? t('FILTER.NO_MIN')
                  : `$${numberWithCommas(state.minPrice)}`}
              </span>
            </div>
            <span className="text-ink-200">—</span>
            <div className="flex-1 rounded-xl border border-ink-100/20 px-3 py-2.5">
              <span className="block text-[10px] font-medium tracking-wider text-ink-200 uppercase">
                {t('FILTER.UNTIL_PRICE')}
              </span>
              <span className="block text-sm font-semibold text-ink-800">
                {!state.maxPrice || state.maxPrice >= 100000
                  ? t('FILTER.NO_MAX')
                  : `$${numberWithCommas(state.maxPrice)}`}
              </span>
            </div>
          </div>
          <div className="mt-2 px-2">
            <PanelSlider
              min={0}
              step={1}
              max={180}
              value={[
                state.minPrice === MIN_PRICE || !state.minPrice
                  ? MIN_PRICE
                  : Math.log10((state.minPrice || 1) + 1) * 30,
                Math.log10((state.maxPrice || MAX_PRICE) + 1) * 30
              ]}
              onChange={(event, value) => {
                event.stopPropagation()
                if (Array.isArray(value)) {
                  const newValue = value as number[]
                  const minPrice =
                    newValue[0] === 0 ? 0 : Math.pow(10, newValue[0] / 30) - 1
                  const maxPrice = Math.pow(10, newValue[1] / 30) - 1
                  setState({
                    ...state,
                    minPrice: Math.round(minPrice),
                    maxPrice: Math.round(maxPrice)
                  })
                }
              }}
              valueLabelFormat={valueLabelFormat}
              getAriaValueText={valueLabelFormat}
            />
          </div>
        </section>

        {/* Sort By Section */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink-800">
            {t('SEARCH_MANAGER.SORT')}
          </h3>
          <BaseSelect
            id="sort"
            fullWidth
            entries={sortByOptions}
            value={state.sortBy ?? SortBy.Recommended}
            onChange={(event) => {
              setState({
                ...state,
                sortBy: event.target.value as SortBy
              })
            }}
          />
        </section>
      </div>

      {/* Footer */}
      <div
        className="shrink-0 border-t border-ink-100/15 px-5 pt-3"
        style={{
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))'
        }}
      >
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="rounded text-sm font-medium text-ink-700 underline-offset-4 transition-colors hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
            onClick={onClear}
          >
            {t('FILTER.RESET_BUTTON')}
          </button>
          <button
            type="button"
            className="rounded-full bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white-500 transition-colors hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
            onClick={onSubmit}
          >
            {t('FILTER.APPLY_BUTTON')}
          </button>
        </div>
      </div>
    </div>
  )
}

function FiltersDisplay() {
  const { t } = useTranslation('common')
  const { filtersOpen, setFiltersOpen } = useFilterProvider()
  const { isDownMd } = useBreakpoints()

  const surface: FiltersSurface = isDownMd ? 'mobile_drawer' : 'desktop_modal'

  // The open itself is the discovery signal (see CLAUDE.md "Instrument
  // public-page interactions by default"): `select_category` only fires on
  // Apply, so without this event "never opened the drawer" and "opened and
  // bailed without applying" are indistinguishable — exactly the question
  // behind mobile's 3.6× lower per-searcher filter usage. Fires on every
  // visibility transition to open (not session-deduped, matching
  // size_modal_open).
  useEffect(() => {
    if (!filtersOpen) return
    ga.event('filters_open', { surface })
  }, [filtersOpen, surface])

  return isDownMd ? (
    <StyledDrawer
      anchor="top"
      open={filtersOpen}
      onClose={() => setFiltersOpen(false)}
    >
      <div className="grid h-full grid-rows-[64px_minmax(0,1fr)] overflow-hidden">
        <div />
        <Filters setFiltersOpen={setFiltersOpen} surface={surface} />
      </div>
    </StyledDrawer>
  ) : (
    <ModalDrawer
      isOpen={filtersOpen}
      setIsOpen={setFiltersOpen}
      title={t('FILTER.FILTER')}
    >
      <div>
        <Filters setFiltersOpen={setFiltersOpen} surface={surface} />
      </div>
    </ModalDrawer>
  )
}

export default React.memo(FiltersDisplay)
